import { useTranslation } from 'react-i18next';
import { humanizeEnum } from '@/lib/utils';
import { RecordLink } from './RecordLink';
import { Row, SourceRow, when, type RecordSource } from './bodyParts';

/** POS-I247: signed change. The API stores quantity as a positive number for every type, so the sign comes from the stock before and after (the type only when those are missing). */
export function signedMovementChange(m: { type: string; quantity: number; beforeQty?: number | null; afterQty?: number | null }): number {
  if (m.beforeQty != null && m.afterQty != null) return m.afterQty - m.beforeQty;
  const abs = Math.abs(m.quantity);
  return ['ADJUSTMENT_IN', 'PURCHASE', 'RETURN', 'SALE_VOID'].includes(m.type) ? abs : abs === 0 ? 0 : -abs;
}

export interface StockMovementDetail {
  id: string;
  type: string;
  quantity: number;
  beforeQty: number;
  afterQty: number;
  reason?: string | null;
  createdAt: string;
  product?: { name: string; sku?: string | null } | null;
  location?: { name: string } | null;
  serialNumber?: { serialNo: string } | null;
  createdByName?: string | null;
  journalEntry?: RecordSource | null;
  /** POS-I244: links between a movement and the reversal that undid it. */
  reversal?: { reversedBy: { id: string; createdAt: string } | null; reverses: { id: string } | null } | null;
}

const formatSigned = (n: number) => (n > 0 ? `+${n}` : String(n));

/** Read-only stock movement for the record viewer (POS-I238). Carries the Reversed by / Reverses links (POS-I244). */
export function StockMovementDetailBody({ data }: { data: StockMovementDetail }) {
  const { t } = useTranslation('records');
  return (
    <div className="space-y-5 text-sm">
      <dl className="space-y-1.5">
        <Row label={t('rvProduct')}>
          {data.product?.name}
          {data.product?.sku && <span className="ms-2 font-mono text-xs text-muted-foreground" dir="ltr">{data.product.sku}</span>}
        </Row>
        <Row label={t('rvMovementType')}>{humanizeEnum(data.type)}</Row>
        <Row label={t('rvQtyBefore')}><span className="tabular-nums" dir="ltr">{data.beforeQty}</span></Row>
        <Row label={t('rvQtyChange')}><span className="tabular-nums" dir="ltr">{formatSigned(signedMovementChange(data))}</span></Row>
        <Row label={t('rvQtyAfter')}><span className="tabular-nums" dir="ltr">{data.afterQty}</span></Row>
        {data.location?.name && <Row label={t('rvLocation')}>{data.location.name}</Row>}
        {data.serialNumber?.serialNo && <Row label={t('rvSerialNo')}><bdi dir="ltr">{data.serialNumber.serialNo}</bdi></Row>}
        {data.reason && <Row label={t('rvReason')}>{data.reason}</Row>}
        {data.reversal?.reversedBy && (
          <Row label={t('rvReversedBy')}>
            <RecordLink type="stockMovement" id={data.reversal.reversedBy.id} label={when(data.reversal.reversedBy.createdAt)} />
          </Row>
        )}
        {data.reversal?.reverses && (
          <Row label={t('rvReverses')}>
            <RecordLink type="stockMovement" id={data.reversal.reverses.id} label={t('rvOriginalMovement')} />
          </Row>
        )}
        <Row label={t('rvCreatedBy')}>
          {data.createdByName}
          <span className="ms-2 text-xs text-muted-foreground" dir="ltr">{when(data.createdAt)}</span>
        </Row>
      </dl>
      <SourceRow items={[{ label: t('rvJournalEntry'), source: data.journalEntry }]} />
    </div>
  );
}
